import {
  CloseCircleFilled,
  ExclamationCircleOutlined,
  LinkOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Popover, PopoverProps, Spin } from 'antd';
import {
  MsgAttachmentInfo,
  useMsgAttachmentList,
  useRemoveMsgAttachments,
} from './hooks/useUploadAttachment';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { FileIcon } from 'components/FileIcon';

const AttachmentItem = (
  props: Util.PartialPartial<MsgAttachmentInfo, 'storeId'> & {
    onRemove: (id: string) => void;
  },
) => {
  const {
    status,
    id,
    data: { originalFilename },
    onRemove,
  } = props;
  const { t } = useTranslate();

  const { name, ext } = useMemo(() => {
    const dotIndex = originalFilename.lastIndexOf('.');
    const name = originalFilename.substring(0, dotIndex);
    const ext = originalFilename.substring(dotIndex, originalFilename.length);

    return {
      name,
      ext,
    };
  }, [originalFilename]);

  return (
    <div className="w-56 flex items-center justify-between gap-2">
      <div className="flex flex-1 items-center">
        {status === 'uploading' ? <Spin indicator={<LoadingOutlined />} /> : null}
        {status === 'success' ? <FileIcon ext={ext} /> : null}
        {status === 'error' ? <ExclamationCircleOutlined className="!text-error" /> : null}
        <span
          className="max-w-36 ml-1 overflow-hidden text-ellipsis whitespace-nowrap"
          title={originalFilename}
        >
          {name}
        </span>
        <span className="flex-shrink-0">{ext}</span>
      </div>
      <Popconfirm
        title={t('scene.chatWorkspace.messageEditor.confirm.deleteAttachment')}
        onConfirm={() => onRemove(id)}
      >
        <CloseCircleFilled className="!text-content-200" />
      </Popconfirm>
    </div>
  );
};

export const AttachmentUploadComponent = ({
  attachmentList,
  onRemove,
  className,
  ...otherProps
}: {
  attachmentList: Util.PartialPartial<MsgAttachmentInfo, 'storeId'>[];
  onRemove: (id: string) => void;
  className?: string;
} & PopoverProps) => {
  const { t } = useTranslate();

  const hasUploading = useMemo(() => {
    return attachmentList.some((item) => item.status === 'uploading');
  }, [attachmentList]);

  if (attachmentList.length === 0) return null;

  const content = (
    <div className="flex flex-col gap-2">
      {attachmentList.map((data) => (
        <AttachmentItem key={data.id} {...data} onRemove={onRemove} />
      ))}
    </div>
  );

  return (
    <Popover content={content} trigger="hover" {...otherProps}>
      <Button className={className} icon={<LinkOutlined />}>
        {hasUploading
          ? t('common.feedback.uploading')
          : t('common.label.totalItems', { x: attachmentList.length })}
      </Button>
    </Popover>
  );
};

export const AttachmentUpload = ({
  conversationId,
}: {
  conversationId: Conversation.Conversation['conversationId'];
}) => {
  const attachmentList = useMsgAttachmentList(conversationId);
  const remove = useRemoveMsgAttachments();

  return (
    <div className="px-[8px]">
      <AttachmentUploadComponent
        className="!rounded !px-2"
        attachmentList={attachmentList}
        onRemove={(id) => remove(conversationId, id)}
      />
    </div>
  );
};
